import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  fetchPortfolio,
  fetchTradingSettings,
  setSymbolTradeBan,
} from '../../../services/stockai/api/client';
import { formatKrw, goToTrade, normalizeSelectedStock } from '../../../services/stockai/stockUtils';

export default function Portfolio() {
  const navigate = useNavigate();
  const { loggedIn, mock, openLogin, authTick } = useOutletContext() || {};
  const [holdings, setHoldings] = useState([]);
  const [blockedSell, setBlockedSell] = useState([]);
  const [blockedBoth, setBlockedBoth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busySymbol, setBusySymbol] = useState('');
  const [message, setMessage] = useState('');

  const loadAll = useCallback(async () => {
    const [data, settings] = await Promise.all([
      fetchPortfolio(),
      fetchTradingSettings(),
    ]);
    setHoldings(data.portfolio || []);
    const trading = settings.trading || {};
    setBlockedSell(trading.blocked_sell_symbols || []);
    setBlockedBoth(trading.blocked_symbols || []);
  }, []);

  useEffect(() => {
    if (!mock && !loggedIn) {
      setHoldings([]);
      setBlockedSell([]);
      setBlockedBoth([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    loadAll()
      .catch((err) => {
        if (!cancelled) console.warn('[StockAI] 포트폴리오 로드 실패:', err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [loggedIn, mock, authTick, loadAll]);

  const isSellBanned = (symbol) =>
    blockedBoth.includes(symbol) || blockedSell.includes(symbol);

  const toggleSellBan = async (symbol) => {
    const enable = !isSellBanned(symbol);
    setBusySymbol(symbol);
    setMessage('');
    try {
      if (!enable) {
        if (blockedBoth.includes(symbol)) await setSymbolTradeBan(symbol, 'both', false);
        if (blockedSell.includes(symbol)) await setSymbolTradeBan(symbol, 'sell', false);
      } else {
        await setSymbolTradeBan(symbol, 'sell', true);
      }
      await loadAll();
      setMessage(enable ? `${symbol} 매도 금지 적용` : `${symbol} 매도 금지 해제`);
    } catch (err) {
      setMessage(err.message || '매도 금지 변경 실패');
    } finally {
      setBusySymbol('');
    }
  };

  if (loading) return <p className="stockai-card">로딩 중...</p>;

  const totalValue = holdings.reduce((s, h) => s + (h.quantity || 0) * (h.currentPrice || 0), 0);

  return (
    <div>
      {!mock && !loggedIn && (
        <div className="stockai-alert">
          포트폴리오는 로그인 후 조회할 수 있습니다.{' '}
          <button type="button" className="stockai-btn stockai-btn--sm" onClick={openLogin}>로그인</button>
        </div>
      )}
      <h1 className="stockai-page-title">포트폴리오</h1>
      {message && <div className="stockai-alert stockai-alert--info">{message}</div>}
      <div className="stockai-card stockai-stat-card" style={{ marginBottom: '1rem' }}>
        <div className="stockai-stat-label">평가금액 합계</div>
        <div className="stockai-stat-value">{formatKrw(totalValue)}</div>
      </div>
      <div className="stockai-card">
        <table className="stockai-table">
          <thead>
            <tr>
              <th>종목</th>
              <th>수량</th>
              <th>평균단가</th>
              <th>현재가</th>
              <th>손익률</th>
              <th>거래(매도)금지</th>
              <th>주문</th>
            </tr>
          </thead>
          <tbody>
            {holdings.length === 0 ? (
              <tr><td colSpan={7} className="stockai-stat-label">보유 종목이 없습니다.</td></tr>
            ) : (
              holdings.map((h) => {
                const banned = isSellBanned(h.symbol);
                return (
                  <tr key={h.symbol}>
                    <td>
                      {h.name || h.symbol}
                      {banned && <span className="stockai-ban-pill">매도금지</span>}
                    </td>
                    <td>{h.quantity}</td>
                    <td>{formatKrw(h.averagePrice)}</td>
                    <td>{formatKrw(h.currentPrice)}</td>
                    <td className={(h.profit_loss_rate ?? 0) >= 0 ? 'stockai-positive' : 'stockai-negative'}>
                      {(h.profit_loss_rate ?? 0).toFixed(2)}%
                    </td>
                    <td>
                      <label className="stockai-toggle">
                        <input
                          type="checkbox"
                          checked={banned}
                          disabled={busySymbol === h.symbol || (!mock && !loggedIn)}
                          onChange={() => toggleSellBan(h.symbol)}
                        />
                        <span>{banned ? '금지 중' : '허용'}</span>
                      </label>
                    </td>
                    <td>
                      <div className="stockai-action-row">
                        <button
                          type="button"
                          className="stockai-btn stockai-btn--sm"
                          onClick={() => goToTrade(navigate, normalizeSelectedStock(h, 'portfolio'), 'buy')}
                        >
                          매수
                        </button>
                        <button
                          type="button"
                          className="stockai-btn stockai-btn--sm stockai-btn--ghost"
                          disabled={banned}
                          title={banned ? '매도 금지 종목입니다' : undefined}
                          onClick={() => goToTrade(navigate, normalizeSelectedStock(h, 'portfolio'), 'sell')}
                        >
                          매도
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
